import type { FC } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { translate } from "app/filters/i18nFilter";
import {
    MARKET_ANALYSIS_HOOK,
    INDUSTRY_ANALYSIS_METERING_HOOK,
} from "app/pages/segments/components/convert-into-industry-btn/consts";
import { AssetsService } from "app/services/AssetsService";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { isIndustryAnalysisLimitationExceeded } from "pro-features/pages/module start page/src/industry analysis/utils/metering/IndustryAnalysisMetering";

import {
    SaveBtnPopupWrapper,
    TopContent,
    TitleTextContainer,
    Title,
    Subtitle,
    GroupImgContainer,
    BtnsContainer,
    NewIndustryBtn,
    StyledIconContainer,
    NewSegmentBtn,
} from "./StyledComponents";

interface ISaveBtnPopup {
    onNewCustomIndustryClick: () => void;
    onNewSegmentComparisonClick: () => void;
    width?: string;
    isNewIndustryDisabled?: boolean;
    isNewSegmentComparisonDisabled?: boolean;
}

const SaveBtnPopup: FC<ISaveBtnPopup> = ({
    width = "318px",
    onNewCustomIndustryClick,
    onNewSegmentComparisonClick,
    isNewIndustryDisabled = false,
    isNewSegmentComparisonDisabled = false,
}) => {
    const isLimitationExceeded = useExceededLimitation(isIndustryAnalysisLimitationExceeded);
    const isLocked = !isAvailable(swSettings.components["WebMarketResearchOverviewHome"]);
    const isNewIndustryHooked = isLimitationExceeded || isLocked;
    const newIndustryIconName = isNewIndustryHooked ? "sparkles-gray" : "lightning-plus";

    const getHookModalType = (ignoreIsLocked: boolean): string | undefined => {
        if (isLocked && !ignoreIsLocked) {
            return MARKET_ANALYSIS_HOOK;
        } else if (isLimitationExceeded) {
            return INDUSTRY_ANALYSIS_METERING_HOOK;
        }
    };

    const handleButtonClick = (action: () => void, ignoreIsLocked = false) => {
        const hookModalType = getHookModalType(ignoreIsLocked);
        if (hookModalType) {
            openHooksModalV5(hookModalType);
        } else {
            action();
        }
    };

    const dispatchTrackEvent = (eventSubSubName: string) => {
        allTrackers.dispatchTrackEvent("Button", "click", `Segments/Single/${eventSubSubName}`);
    };

    const handleNewIndustryClick = () => {
        if (isNewIndustryDisabled) {
            return;
        }
        dispatchTrackEvent("New industry");
        handleButtonClick(onNewCustomIndustryClick);
    };

    const handleNewSegmentComparisonClick = () => {
        if (isNewSegmentComparisonDisabled) {
            return;
        }
        dispatchTrackEvent("New comparison");
        onNewSegmentComparisonClick();
    };

    return (
        <SaveBtnPopupWrapper
            data-automation="SaveBtnPopup"
            width={width}
        >
            <TopContent>
                <TitleTextContainer>
                    <Title>{translate("segment.analysis.save.btn.popup.title")}</Title>
                    <Subtitle>{translate("segment.analysis.save.btn.popup.subtitle")}</Subtitle>
                </TitleTextContainer>
                <GroupImgContainer>
                    <img src={AssetsService.assetUrl("/images/swicons/illustration-fifty-four-bars.svg")} />
                </GroupImgContainer>
            </TopContent>
            <BtnsContainer>
                <PlainTooltip
                    placement="left"
                    tooltipContent={translate("segment.analysis.save.btn.popup.new.industry.tooltip")}
                    enabled={isNewIndustryDisabled}
                >
                    <div>
                        <NewIndustryBtn
                            onClick={handleNewIndustryClick}
                            isDisabled={isNewIndustryDisabled}
                            data-automation="NewIndustryBtn"
                        >
                            <StyledIconContainer isHookIcon={isNewIndustryHooked}>
                                <SWReactIcons
                                    iconName={newIndustryIconName}
                                    size="xs"
                                />
                            </StyledIconContainer>
                            {translate("segment.analysis.save.btn.popup.new.industry")}
                        </NewIndustryBtn>
                    </div>
                </PlainTooltip>
                <PlainTooltip
                    placement="left"
                    tooltipContent={translate("segment.analysis.save.btn.popup.new.segment.comparison.tooltip")}
                    enabled={isNewSegmentComparisonDisabled}
                >
                    <div>
                        <NewSegmentBtn
                            onClick={handleNewSegmentComparisonClick}
                            isHookIcon={false}
                            isDisabled={isNewSegmentComparisonDisabled}
                            data-automation="NewSegmentBtn"
                        >
                            <StyledIconContainer isHookIcon={false}>
                                <SWReactIcons
                                    iconName="plus"
                                    size="xs"
                                />
                            </StyledIconContainer>
                            {translate("segment.analysis.save.btn.popup.new.segment.comparison")}
                        </NewSegmentBtn>
                    </div>
                </PlainTooltip>
            </BtnsContainer>
        </SaveBtnPopupWrapper>
    );
};

export default SaveBtnPopup;
